import { GameFormat } from 'components/Find/gameFormat';
import type { FindGame, FindGamePlayer } from './types';

/**
 * Adds players who hold a pending invite to a game's drawn roster.
 *
 * Only accepted play requests are seats, so the invited players are not in
 * `teams` — but the host needs to see who they are still waiting on. The split
 * follows the same rule `mapEventToFindGame` uses: Open Play is one roster, the
 * two-sided formats anchor the host on side one and fill left to right.
 */
export const withInvitedPlayers = (
  game: FindGame,
  invited: FindGamePlayer[],
): { game: FindGame; overflow: FindGamePlayer[] } => {
  if (invited.length === 0) return { game, overflow: [] };

  const roster = [...game.teams.flat(), ...invited];

  if (game.format === GameFormat.OpenPlay) {
    // Unlimited Open Play has no seat count, so everyone fits.
    if (game.participantLimit == null) return { game: { ...game, teams: [roster] }, overflow: [] };
    const capacity = Math.max(game.playersPerTeam, 1);
    return {
      game: { ...game, teams: [roster.slice(0, capacity)] },
      overflow: roster.slice(capacity),
    };
  }

  // A host can invite more players than there are seats; the extras don't fit the
  // drawn roster, so they come back separately for the sheet to list.
  const perTeam = game.playersPerTeam;
  return {
    game: { ...game, teams: [roster.slice(0, perTeam), roster.slice(perTeam, perTeam * 2)] },
    overflow: roster.slice(perTeam * 2),
  };
};
